import { getReconciliationLlmReadiness, runReconciliation } from "@/services/api";
import { reconciliationResultMessages } from "@/services/messages";
import { useTabActions, useTabStore } from "@/stores/tab-store";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Триггер запуска сверки (FR-002, FR-004, FR-022).
 *
 * Обёртка над кнопкой «Запустить сверку»:
 * - При клике вызывает `runReconciliation(setId)`.
 * - 202 → колбэк `onStarted(runId, taskId)`. Прогресс-бар рендерит родитель
 *   (detail.tsx) — это устраняет дублирование с restore-after-reload путём
 *   через `isActiveRun + activeTaskId`.
 * - 409 (in-progress, FR-022) → toast + активирует существующий таб
 *   результата `reconciliation-result-${existingRunId}`.
 *   ВАЖНО: backend `RunInProgressResponse` не отдаёт `setName`, поэтому
 *   используется setName из props.
 *
 * Кнопка disabled (с tooltip) для не-`ready` статусов и для активного прогона
 * — управляется через prop `disabled` + `disabledTooltip` от родителя.
 */
export interface RunProgressTriggerProps {
	setId: number;
	setName: string;
	/** Если true — кнопка disabled (для активного прогона). */
	disabled?: boolean;
	/** Tooltip для disabled-кнопки. */
	disabledTooltip?: string;
	/** Если задан — вместо немедленного запуска показывается confirm-диалог
	 * (re-run для `completed`-статуса, FR-006). */
	requireConfirmation?: boolean;
	/** Колбэк показа диалога подтверждения. Должен резолвиться в true для запуска. */
	onConfirmRequest?: () => Promise<boolean>;
	/**
	 * Pre-run guard. Вызывается ПЕРЕД LLM-readiness check и runMutation —
	 * если возвращает `false`, запуск прерывается (без toast'а; родитель
	 * сам показывает причину, например, список pre-flight issues).
	 *
	 * Используется для слияния "Подготовить к запуску" + "Запустить сверку"
	 * в одну кнопку: detail.tsx передаёт guard, который вызывает
	 * `prepareReconciliationSet(setId)` и возвращает `result.ok`.
	 */
	runGuard?: () => Promise<boolean>;
	/**
	 * Внешнее состояние "что-то идёт" (например, prepareMutation.isPending
	 * в родителе). Складывается с внутренними flags по OR — кнопка
	 * показывает loader и disabled.
	 */
	extraPending?: boolean;
	/** Колбэк сразу после 202 — родитель использует runId+taskId, чтобы
	 * отрендерить `RunProgressBar` без ожидания refetch'а `recon-runs`. */
	onStarted?: (runId: string, taskId: string) => void;
}

export function RunProgressTrigger({
	setId,
	setName,
	disabled = false,
	disabledTooltip,
	requireConfirmation = false,
	onConfirmRequest,
	runGuard,
	extraPending = false,
	onStarted,
}: RunProgressTriggerProps) {
	const messages = reconciliationResultMessages.run;
	const queryClient = useQueryClient();
	const { openReconciliationResultTab, markRunResultStale } = useTabActions();
	// Sprint 4 / A6.5b — состояние диалога LLM-readiness.
	const [llmConfirm, setLlmConfirm] = useState<{ pending: number; total: number } | null>(null);
	const [llmCheckPending, setLlmCheckPending] = useState(false);

	/**
	 * Помечает все открытые `reconciliation-result` табы текущего setId
	 * флагом `stale: true` (FR-021, T064). Вызывается перед стартом нового
	 * прогона, чтобы старые табы получили overlay+баннер.
	 *
	 * Не помечает таб с новым runId (его ещё нет в момент вызова).
	 */
	const markPreviousResultsStale = () => {
		const tabs = useTabStore.getState().tabs;
		for (const tab of tabs) {
			if (tab.type === "reconciliation-result" && tab.setId === setId && tab.runId) {
				markRunResultStale(tab.runId);
			}
		}
	};

	const runMutation = useMutation({
		mutationFn: () => runReconciliation(setId),
		onSuccess: (response) => {
			if (response.status === "started") {
				// FR-021: помечаем старые табы результата как устаревшие ПЕРЕД
				// открытием нового. Если старых табов нет — no-op.
				markPreviousResultsStale();
				onStarted?.(response.runId, response.taskId);
				toast.success(messages.toast_started);
				queryClient.invalidateQueries({ queryKey: ["recon-runs", setId] });
			} else {
				// FR-022: 409 — уже идёт прогон. Показываем toast и активируем
				// (или открываем) таб с existingRunId. Backend гарантирует, что
				// существует ровно один pending/running прогон для setId.
				toast.info(reconciliationResultMessages.concurrent.toast);
				openReconciliationResultTab(setId, response.existingRunId, setName);
				queryClient.invalidateQueries({ queryKey: ["recon-runs", setId] });
			}
		},
		onError: (err) => {
			const message = err instanceof Error ? err.message : String(err);
			toast.error(message);
		},
	});

	// Покрывает окно между разными async-фазами клика (confirm → runGuard →
	// llm-readiness → runMutation.mutate). Без этого `extraPending` родителя
	// успевает упасть в false ДО того, как runMutation.isPending встанет в true,
	// и кнопка на миг моргает без spinner'а.
	const [clickPending, setClickPending] = useState(false);

	const proceedRun = () => {
		setLlmConfirm(null);
		runMutation.mutate();
	};

	const handleClick = async () => {
		if (clickPending) return; // защита от двойного клика
		setClickPending(true);
		try {
			if (requireConfirmation && onConfirmRequest) {
				const confirmed = await onConfirmRequest();
				if (!confirmed) return;
			}

			// Pre-run guard: если родитель передал hook (например, prepare-проверка
			// перед запуском), даём ему шанс прервать запуск.
			if (runGuard) {
				const allowed = await runGuard();
				if (!allowed) return;
			}

			// Sprint 4 / A6.5b: pre-flight для LLM-readiness. Если есть блоки в
			// статусе `pending` — показываем модал «LLM ещё проверяет, запустить
			// сверку всё равно?». Сетевая ошибка readiness — не блокирует запуск:
			// продолжаем как раньше.
			try {
				setLlmCheckPending(true);
				const readiness = await getReconciliationLlmReadiness(setId);
				setLlmCheckPending(false);
				if (readiness.pending > 0) {
					setLlmConfirm({ pending: readiness.pending, total: readiness.total });
					return; // ждём решения пользователя
				}
			} catch {
				setLlmCheckPending(false);
				// readiness не загрузился — не блокируем запуск.
			}

			runMutation.mutate();
		} finally {
			setClickPending(false);
		}
	};

	return (
		<>
			<button
				type="button"
				disabled={
					disabled ||
					runMutation.isPending ||
					llmCheckPending ||
					extraPending ||
					clickPending
				}
				title={disabled ? disabledTooltip : undefined}
				onClick={handleClick}
				className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
			>
				{(runMutation.isPending || llmCheckPending || extraPending || clickPending) && (
					<Loader2 className="mr-2 h-4 w-4 animate-spin" />
				)}
				{messages.button_label}
			</button>

			{/* Sprint 4 / A6.5b — LLM readiness confirm dialog. */}
			{llmConfirm !== null && (
				<dialog
					open
					className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md rounded-lg bg-white p-6 shadow-xl backdrop:bg-black/50"
					aria-labelledby="llm-readiness-dialog-title"
				>
					<h3 id="llm-readiness-dialog-title" className="mb-2 text-base font-semibold text-slate-900">
						LLM-проверка ещё не завершена
					</h3>
					<p className="mb-4 text-sm text-slate-600">
						{llmConfirm.pending} из {llmConfirm.total} блоков актов ещё проходят
						LLM-проверку. Запустить сверку всё равно? Часть аномальных блоков
						может попасть в отчёт без коррекции.
					</p>
					<div className="flex justify-end gap-2">
						<button
							type="button"
							onClick={() => setLlmConfirm(null)}
							className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
						>
							Подождать
						</button>
						<button
							type="button"
							onClick={proceedRun}
							className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
						>
							Запустить как есть
						</button>
					</div>
				</dialog>
			)}
		</>
	);
}
